// --- Core ---
import {Suspense, useEffect, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
// --- Material UI ---
import {Box, Button, Card, Grid, Skeleton, Stack, Typography} from "@mui/material";
// --- SaniQ ---
import Logo from "shared/layout/Logo";
import Loading from "shared/Loading";
import {Image} from "src/helpers/RenderUtil";
import {Footer} from "src/modules/app/components/Footer";
import {fetchStartContent} from "src/modules/tenant_login/requests/Login";
import {currentTenant} from "helpers/Application";
import {StartContent} from "../../../models/Login";
import config from "config";

const TenantStartContentLoading = () => (
  <Stack>
    <Skeleton height={50}/>
    <Skeleton sx={{height: 300}}/>
    <Skeleton height={50}/>
    <Skeleton/>
    <Skeleton/>
    <Skeleton width={150} height={50}/>
  </Stack>
);

const TenantLoginLayout = ({children}) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(true);
  const [startContent, setStartContent] = useState<StartContent>();

  useEffect(() => {
    document.title = intl.formatMessage({id: "tenant.login.title", defaultMessage: "SaniQ Login"});
  }, [intl]);

  useEffect(() => {
    fetchStartContent(currentTenant()).then(async ({content}) => {
      const startItem =
        content.length === 0
          ? (await fetchStartContent("global")).content[0]
          : content[0];
      setStartContent(startItem);
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  return (
    <>
      <Grid
        container
        sx={
          {
            justifyContent: "space-between",
            alignItems: "stretch",
            flexGrow: 1,
            height: "100%",
            minHeight: "100vh"
          }
        }
      >
        <Grid
          item sx={{display: {xs: 'none', sm: 'none', md: 'block'}}}
          md={4}
          style={{background: "linear-gradient(0deg, rgba(33,150,243,0.3) 0%, rgba(33,150,243,0.05) 100%) "}}
        >
          <Stack
            spacing={2}
            sx={
              {
                justifyContent: "center",
                m: 4,
                mr: 2,
                p: 2,
                minHeight: '100vh',
                height: "100%"
              }
            }
          >
            <Box sx={{position: "absolute", top: theme => theme.spacing(4)}}>
              <Logo/>
            </Box>
            {
              loading ? <TenantStartContentLoading/> :
                (
                  <>
                    <Typography variant={"h3"} sx={{fontFamily: 'Montserrat', pb: 2, pt: 8}}>
                      {startContent?.display || "Personal Digital Medicine"}
                    </Typography>
                    {
                      <Image
                        sx={{height: "auto", width: "75%", borderRadius: 4}}
                        //NOTE(Keune): To prevent layout shifting when loading images, the width and height of the source must be specified
                        width={960} height={640}
                        alt="SaniQ"
                        src={
                          startContent?.media?.[0]?.url ?
                            config.qurasoftApiBaseUrl + startContent?.media?.[0]?.url
                            :
                            "https://cdn.saniq.org/saniq/banner/2024.webp"
                        }
                      />
                    }
                    <Typography
                      variant={"h5"}
                      sx={{fontFamily: 'Montserrat', fontWeight: "bold"}}
                    >{startContent?.title || "Remote Patient Monitoring"}
                    </Typography>
                    <Typography variant={"body1"} sx={{whiteSpace: 'pre-line'}}>
                      {
                        startContent?.description ||
                        <FormattedMessage
                          id={"tenant.login.description"}
                          defaultMessage={"Discover the future of healthcare: Our Remote Patient Monitoring system enables continuous monitoring of health data from home. With real-time feedback and early intervention, we improve care and increase quality of life. All data is centralized in your SaniQ practice instance."}
                        />
                      }
                    </Typography>
                    {
                      startContent?.externalLink &&
                      <Button
                        href={startContent?.externalLink || "https://qurasoft.de/saniq"}
                        target="_blank"
                        variant={"outlined"}
                        sx={{mt: 2, alignSelf: "baseline"}}
                      >
                        <Typography variant={"body1"}>
                          {startContent?.externalLinkText || <FormattedMessage id={"tenant.login.discover_more"} defaultMessage={"Discover more"}/>}
                        </Typography>
                      </Button>
                    }
                  </>
                )
            }
          </Stack>
        </Grid>
        <Grid
          xs={12}
          sm={12}
          md={8}
          item
        >
          <Grid
            container
            sx={
              {
                alignItems: "center",
                justifyContent: "center",
                minHeight: '100vh',
                height: "100%"
              }
            }
          >
            <Grid
              item xs={10}
              sm={8}
              md={7}
              lg={5}
            >
              <Card variant={"outlined"}>
                <Suspense fallback={<Loading/>}>
                  {children}
                </Suspense>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Footer sx={{mt: 0, pt: 3, pb: 2}}/>
    </>
  );
};

export default TenantLoginLayout;
